import { InvalidError } from '@ember-data/adapter/error';
import hash from 'object-hash';

import type StoreService from '@ember-data/store';
import type IntegrationsConfigurationModel from 'admin-panel/models/integrations/configuration';
import type IntegrationsConfigurationRevisionModel from 'admin-panel/models/integrations/configuration-revision';
import type { IntegrationConfig } from 'admin-panel/models/integrations/configuration';

export class RevisionSaveError extends Error {
  constructor(readonly reason: unknown) {
    super('The integration was saved but its configuration was not');
  }
}

interface AdapterFieldError {
  source?: { pointer?: string };
}

const versionWasTaken = (error: unknown) =>
  error instanceof InvalidError &&
  (error as InvalidError & { errors: AdapterFieldError[] }).errors.some(
    ({ source }) => source?.pointer?.endsWith('/version')
  );

export const configFingerprint = (config: IntegrationConfig): string =>
  hash(config);

export const hasUnsavedAttributes = (
  configuration: IntegrationsConfigurationModel
): boolean => configuration.hasDirtyAttributes as unknown as boolean;

export const latestRevision = async (
  store: StoreService,
  configuration: IntegrationsConfigurationModel
): Promise<IntegrationsConfigurationRevisionModel | undefined> => {
  if (!configuration.id) {
    throw new Error(
      'Refusing to read revisions for an unsaved integration configuration: a blank ransack filter returns every revision'
    );
  }

  const revisions = await store.query('integrations/configuration-revision', {
    'filter[configuration_id_eq]': configuration.id,
    'filter[s]': 'version desc',
    limit: 1,
  });

  return revisions.toArray()[0];
};

const saveRevision = async (
  store: StoreService,
  configuration: IntegrationsConfigurationModel,
  config: IntegrationConfig,
  version: number
) => {
  const revision = store.createRecord('integrations/configuration-revision', {
    configuration,
    version,
    config,
  });

  try {
    return await revision.save();
  } catch (error) {
    revision.unloadRecord();
    throw error;
  }
};

export const createRevision = async (
  store: StoreService,
  configuration: IntegrationsConfigurationModel,
  config: IntegrationConfig,
  version?: number
): Promise<IntegrationsConfigurationRevisionModel> => {
  let requestedVersion = version;

  if (requestedVersion === undefined) {
    const stored = await latestRevision(store, configuration);

    if (
      stored &&
      configFingerprint(stored.config) === configFingerprint(config)
    ) {
      return stored;
    }

    requestedVersion = (stored?.version ?? 0) + 1;
  }

  try {
    return await saveRevision(store, configuration, config, requestedVersion);
  } catch (error) {
    const committed = await latestRevision(store, configuration);

    if (
      committed &&
      configFingerprint(committed.config) === configFingerprint(config)
    ) {
      return committed;
    }

    if (!versionWasTaken(error)) {
      throw error;
    }

    return saveRevision(
      store,
      configuration,
      config,
      (committed?.version ?? 0) + 1
    );
  }
};

export const saveConfiguration = async (
  store: StoreService,
  configuration: IntegrationsConfigurationModel,
  {
    config,
    previousConfig,
  }: { config: IntegrationConfig; previousConfig?: IntegrationConfig }
): Promise<IntegrationsConfigurationRevisionModel | undefined> => {
  if (hasUnsavedAttributes(configuration)) {
    await configuration.save();
  }

  if (
    previousConfig &&
    configFingerprint(config) === configFingerprint(previousConfig)
  ) {
    return undefined;
  }

  try {
    return await createRevision(store, configuration, config);
  } catch (error) {
    throw new RevisionSaveError(error);
  }
};
